import { useRef, useState } from "react";
import { Plus, Trash2, Save, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const norm = (s) => s.toUpperCase().split(/\s+/).filter(Boolean).join(" ");
export const lineTags = (rows) => {
  const counts = {};
  return rows.map((d) => { const k = norm(d.speaker || ""); counts[k] = (counts[k] || 0) + 1; return `{${k} ${counts[k]}}`; });
};
const usedTags = (action) => [...(action || "").matchAll(/\{\s*([^{}]*?\S)\s+(\d+)\s*\}/g)].map((m) => `{${norm(m[1])} ${Number(m[2])}}`);

const DialogueRows = ({ rows, onChange, sid, tags, used, onInsert }) => (
  <div className="space-y-2">
    {rows.map((d, i) => (
      <div key={i} className="flex gap-2 items-center">
        <button type="button" data-testid={`dlg-tag-${sid}-${i}`} onMouseDown={(e) => e.preventDefault()} onClick={() => onInsert(tags[i])} title="Insert into the action at the cursor"
          className={`shrink-0 font-mono text-[10px] px-2 h-7 rounded-full border transition-colors ${used.includes(tags[i]) ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-[#3B4662] text-slate-400 hover:border-cyan-400"}`}>
          {tags[i]}
        </button>
        <Input data-testid={`dlg-speaker-${sid}-${i}`} className="w-32 shrink-0 font-mono text-xs uppercase" value={d.speaker}
          onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, speaker: e.target.value } : r)))} />
        <Input data-testid={`dlg-line-${sid}-${i}`} value={d.line}
          onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, line: e.target.value } : r)))} />
        <button data-testid={`dlg-remove-${sid}-${i}`} onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400 px-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    ))}
    <button data-testid={`dlg-add-${sid}`} onClick={() => onChange([...rows, { speaker: rows.at(-1)?.speaker || "", line: "" }])}
      className="text-xs text-cyan-400 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" />Add line</button>
  </div>
);

export const SceneEditor = ({ scene, onSaved }) => {
  const [f, setF] = useState({
    heading: scene.heading, action: scene.action, camera: scene.camera,
    characters: scene.characters.join(", "), dialogue: scene.dialogue,
  });
  const actionRef = useRef(null);
  const tags = lineTags(f.dialogue);
  const used = usedTags(f.action);
  const unknown = used.filter((t) => !tags.includes(t));
  const words = f.dialogue.reduce((n, d) => n + d.line.split(/\s+/).filter(Boolean).length, 0);
  const insert = (tag) => {
    const el = actionRef.current;
    const a = f.action || "";
    const at = el && document.activeElement === el ? el.selectionStart : a.length;
    const pre = at > 0 && !/\s$/.test(a.slice(0, at)) ? " " : "";
    const post = /^\s/.test(a.slice(at)) || at === a.length ? "" : " ";
    const next = `${a.slice(0, at)}${pre}${tag}${post}${a.slice(at)}`;
    const caret = at + pre.length + tag.length + post.length;
    setF({ ...f, action: next });
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret); } });
  };
  const placeAll = () => {
    const missing = tags.filter((t) => !used.includes(t));
    if (missing.length) setF({ ...f, action: `${(f.action || "").trimEnd()} ${missing.join(" ")}`.trim() });
  };
  const save = async () => {
    try {
      await http.patch(`/scenes/${scene.id}`, { ...f, characters: f.characters.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success(`Scene ${scene.number} saved`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sid = scene.id;
  return (
    <div className="space-y-3 pt-3" data-testid={`scene-editor-${sid}`}>
      <Input data-testid={`scene-heading-${sid}`} value={f.heading} onChange={(e) => setF({ ...f, heading: e.target.value })} placeholder="Scene heading" />
      <Textarea ref={actionRef} data-testid={`scene-action-${sid}`} rows={4} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })}
        placeholder="What happens on screen, e.g. Dotty steps in. {DOTTY 1} Spooky turns slowly. {SPOOKY 1}" />
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500" data-testid={`scene-tag-hint-${sid}`}>
        <MessageSquareQuote className="w-3.5 h-3.5 text-cyan-400" />
        Click a line tag to drop it into the action where that line should be spoken. Untagged lines play after the tagged ones.
        {f.dialogue.length > 0 && (
          <button type="button" data-testid={`scene-place-all-${sid}`} onClick={placeAll} className="text-cyan-400 hover:underline">Add untagged lines in order</button>
        )}
      </div>
      {unknown.length > 0 && (
        <div className="text-[11px] text-amber-300" data-testid={`scene-unknown-tags-${sid}`}>
          {unknown.join(", ")} {unknown.length > 1 ? "don't" : "doesn't"} match any line below and will be ignored.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        <Input data-testid={`scene-camera-${sid}`} value={f.camera} onChange={(e) => setF({ ...f, camera: e.target.value })} placeholder="Camera notes" />
        <Input data-testid={`scene-characters-${sid}`} value={f.characters} onChange={(e) => setF({ ...f, characters: e.target.value })} placeholder="Characters on screen (comma separated)" />
      </div>
      <DialogueRows sid={sid} rows={f.dialogue} tags={tags} used={used} onInsert={insert} onChange={(dialogue) => setF({ ...f, dialogue })} />
      <div className="flex items-center gap-3">
        <span className={`font-mono text-[11px] ${words > 45 ? "text-red-400" : "text-slate-500"}`} data-testid={`scene-word-count-${sid}`}>
          {words} spoken words {words > 45 ? "— too long for 20s, consider splitting" : ""}
        </span>
        <Button data-testid={`scene-save-${sid}`} size="sm" onClick={save} className="ml-auto rounded-full"><Save className="w-3.5 h-3.5 mr-1.5" />Save scene</Button>
      </div>
    </div>
  );
};
